import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactElement } from 'react';
import { json, redirect, type LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData, useLocation, useSearchParams } from '@remix-run/react';
import invariant from 'tiny-invariant';
import { cn } from '@rocco/utils/cn';
import type { MetaDescriptor } from '@remix-run/node';
import {
    createMetaDescription,
    createMetaTitle,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { fullCountryNameFromCode } from '~/modules/seo/util/localisation';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Box, Container, useStyles } from '@archipro-design/aria';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
} from '~/modules/search';
import type { ProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { mapESProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { getDirectoryPageMeta } from '@modules/category/api';
import type { AdsLoaderData } from '@modules/adverts/api';
import { adRequest, adsRequest } from '@modules/adverts/api';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { parseURL } from '@archipro-website/top-navigation';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '~/modules/search/hook/use-search-filters';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import { useTracker } from '@archipro-website/tracker';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { getSession } from '~/modules/root/util/session.server';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProjectCategories } from '~/modules/projects-category/api';
import * as S from '~/modules/projects-category/components/project-category-container/ProjectCategoryContainer.style';
import { advertCreativeProjectTileView } from '@modules/products-category/util/advert-creative-project-tile-view';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getRouteData } from '~/modules/root/util/get-route-data';

import {
    hasFeature,
    useFeature,
    useTileHotButtons,
    type APHandle,
} from '~/modules/root';
import getShowProfessionalArchiproConnectBanner from '~/utils/get-show-professional-archipro-connect-banner';

import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getUrlHasFilters } from '~/modules/search/util/get-url-has-filters.server';
import { professionalEvenShuffleAds } from '~/utils/adHelper';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import {
    isLocationIndexPage,
    noIndexProjectCategory,
} from '@modules/seo/util/no-index-project-category';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createProjectsItemListSchema } from '~/modules/seo/schema/projects-item-list-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getHeroPotm } from '@modules/projects-category/api/get-hero-potm.server';
import DirectoryHeroPotmDesktop from '@modules/projects-category/components/directory-hero-potm/DirectoryHeroPotmDesktop';
import { getGridPotms } from '@modules/projects-category/api/get-grid-potms.server';
import { getPreloadImageLink } from '@modules/images';
import DirectoryHeroPotmMobile from '@modules/projects-category/components/directory-hero-potm/DirectoryHeroPotmMobile';
import { usePotmHeroEnabled } from '@modules/projects-category/util/potm-hero-enabled';
import { buildProjectTileGrid } from '~/utils/buildProjectTileGrid';
import { renderProjectTileGrid } from '~/utils/renderProjectTileGrid';
import { getProjectsQuerySize } from '~/utils/get-projects-query-size';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import type { ProjectTileViewModel as ProjectTileViewModelRocco } from '@rocco/ui/tile-project/view-models/project';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { getProjectsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-project';
import { getProjectBannerImages } from '@modules-rocco/projects/api/get-project-banner-images.server';
import { PROJECT_TILE_BANNER_SELECTOR_FLAG } from '~/modules/directory/hook/use-set-item-banner';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import {
    getIsArchiproStaffFromMember,
    getProjectCategoryIgnoreFilterKeys,
    getRedirectUrlWithoutProjectProfessionalsFilter,
    shouldHideUsProjectProfessionalsFilter,
    stripProjectProfessionalsFromSearchFilters,
} from '~/modules/project/util/us-project-professionals-filter-gating';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import { VirtualizedGrid } from '~/components/VirtualizedGrid';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { getDirectoryFeaturedProjects } from '~/modules/directory/api/get-directory-featured-project.server';
import { useToggleFeaturedProject } from '~/modules/directory/hook/use-toggle-featured-project';
import { useLibraryRemovalUi } from '~/modules/directory/hook/use-library-removal-ui';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { getProjectCategories as getProjectCategoriesRocco } from '~/modules-rocco/projects/api/get-project-categories.server';
import { parseURL as parseURLRocco } from '@modules-rocco/url/api/parse-url';
import { mapCategoriesToTileViewModel } from '@modules-rocco/categories/data-mappers/category-to-tile';
import { ExploreAllCategories } from '~/ui-rocco/pages/products-page/ExploreAllCategories';

export const PROJECT_TILE_PAGING_WIDTH = 175;
export const PROJECT_TILE_BULLET_SIZE = 11;
export const PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD = 1;
export const PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD = 100;

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `${data?.directoryPageMeta?.category} projects`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page,
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO',
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Featuring ${
                data?.directoryPageMeta?.category
            } projects from the most talented professionals in ${fullCountryNameFromCode(
                rootData.localisation.country,
            )} with stunning photos and amazing designs to inspire you.`,
        ]);

        const pageUrl = data?.url ?? '';
        const isIndexPage = isLocationIndexPage(pageUrl);
        const projectsListSchema = isIndexPage
            ? createProjectsItemListSchema(props)
            : [];
        const FAQSchema = isIndexPage
            ? createFAQSchema(data?.localisation?.FAQSchemas ?? [])
            : [];
        const ldSchema = createLDSchema([projectsListSchema, FAQSchema]);

        // Preloading disabled - eager loading with fetchPriority="high" provides similar performance benefits
        let preloadImageLinks: MetaDescriptor[] = [];

        if (data?.heroPotm?.videoCoverDesktop) {
            const preloadHero = getPreloadImageLink(
                data.heroPotm.videoCoverDesktop,
                undefined,
                getImagesSizes(1920),
            );
            preloadImageLinks = [...preloadHero, ...preloadImageLinks];
        }

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];

        return noIndexProjectCategory(metaDescriptors, pageUrl);
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    showStickyNavAfterPosition: 500,
    hideContactArchiPro: false,
    // Big-A unified header (BC-4480) — matches the approved directory mock.
    header: { showLogomark: true },
};

const DEFAULT_PROJECT_PAGE_SIZE = 36;
const MAX_ADS_PER_PAGE = 3;
const MAX_VERTICAL_TILES_PER_PAGE = 4;
const MAX_VERTICAL_AD_TILES_PER_PAGE = 1;
const MAX_SPONSORED_AD_TILES_DISPLAYED = 3;
const DEFAULT_SORT = 'featured';
export const PROJECT_TILE_EXPANDED_WIDTH = 613;
export const PROJECT_TILE_COLLAPSED_WIDTH = 455;

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { appDisplayMode, user, featureFlags } = context;
    const isArchiproStaff = getIsArchiproStaffFromMember(user);
    const hideProfessionalsFilter = shouldHideUsProjectProfessionalsFilter({
        countryCode: context.localisation.country,
        isArchiproStaff,
    });

    if (hideProfessionalsFilter) {
        const cleanedUrl = getRedirectUrlWithoutProjectProfessionalsFilter(
            request.url,
        );
        if (cleanedUrl) {
            return redirect(cleanedUrl);
        }
    }

    const isMobile = appDisplayMode === 'mobile';
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    const pageParam = Number(searchParams.get('page'));
    const page = pageParam && isFinite(pageParam) ? pageParam : 1;
    const { basePath } = parseURL(url.pathname);
    const session = await getSession(request);
    const hasFilters = getUrlHasFilters(url);

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'project',
            'directory',
            { pageSize },
            DEFAULT_SORT,
        );
        if (hideProfessionalsFilter) {
            stripProjectProfessionalsFromSearchFilters(queryParams.filters);
        }
        const isFoo = context.config.search.domain === 'foo';
        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });
        const searchPromise = !isFoo
            ? querySearchServiceSearch({ context, queryParams })
            : null;
        const aggregationPromise = !isFoo
            ? querySearchServiceAggregate({
                  context,
                  queryParams: aggregationProps,
              })
            : null;

        return Promise.all([
            queryParams,
            aggregationProps,
            searchPromise,
            aggregationPromise,
        ]);
    };

    const getAdsGridPotmsAndEsResults = async () => {
        const [ads, sponsoredAds, gridPotms] = await Promise.all([
            adsRequest({
                type: 'DirectoryWithProjectPlacementGroup',
                requestUrl: request.url,
                ...context,
                request,
            }),
            adRequest({
                type: 'SponsorshipDirectoryPlacement',
                requestUrl: request.url,
                ...context,
                request,
            }),
            getGridPotms(context),
        ]);
        const validVerticalAds = ads?.VerticalTilePlacement?.filter(
            (ad) => !!ad?.id,
        );
        const queryESPageSize = getProjectsQuerySize({
            sponsoredAdsCount: sponsoredAds?.length ?? 0,
            gridPotmCount: gridPotms?.length ?? 0,
            verticalAdsCount: validVerticalAds?.length ?? 0,
            hasFilters,
            page,
            config: {
                defaultProjectPageSize: DEFAULT_PROJECT_PAGE_SIZE,
                maxVerticalTilesPerPage: MAX_VERTICAL_TILES_PER_PAGE,
                maxVerticalAdTilesPerPage: MAX_VERTICAL_AD_TILES_PER_PAGE,
                maxSponsoredAdTilesDisplayed: MAX_SPONSORED_AD_TILES_DISPLAYED,
            },
        });
        return {
            ads: {
                ...ads,
                VerticalTilePlacement: validVerticalAds,
            } as AdsLoaderData,
            sponsoredAds,
            gridPotms,
            query: await queryES(queryESPageSize),
        };
    };

    const [
        directoryPageMeta,
        // rootDirectoryPageMeta will be used as a fallback for missing category descriptions
        rootDirectoryPageMeta,
        projectCategory,
        recentlyViewedResult,
        localisation,
        heroPotm,
        activeDirectoryCategoriesCount,
        featuredProjectsResult,
        {
            ads,
            sponsoredAds,
            gridPotms,
            query: [queryParams, aggregationProps, searchResult, aggregations],
        },
        rootProjectCategoriesResult,
    ] = await Promise.all([
        getDirectoryPageMeta({
            url,
            ...context,
        }),
        // fetch the root projects category meta to use its description as a fallback
        getDirectoryPageMeta({
            url: new URL('/projects', request.url),
            ...context,
        }),
        getProjectCategories({
            ...context,
            request,
        }),
        getRecentlyViewedServer({ type: 'Projects' }, context),
        getPageLocalisation({ context, request }),
        getHeroPotm(context),
        getActiveDirectoryCategoriesCount({}, context),
        getDirectoryFeaturedProjects(context),
        getAdsGridPotmsAndEsResults(),
        // Fetch root-level project categories for ExploreAllCategories (desktop only)
        !isMobile
            ? getProjectCategoriesRocco(
                  { urlInfo: parseURLRocco('/projects') },
                  context,
              )
            : null,
    ]);

    const _disableHeroPotm = true;
    const hasHeroPotmMedia =
        !_disableHeroPotm &&
        (isMobile
            ? !!(
                  heroPotm?.videoCoverMobile &&
                  (heroPotm?.mobileVideoMp4 || heroPotm?.mobileVideoWebm)
              )
            : !!(
                  heroPotm?.videoCoverDesktop &&
                  (heroPotm?.teaserVideoMp4 || heroPotm?.teaserVideoWebm)
              ));

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: DEFAULT_PROJECT_PAGE_SIZE,
    });

    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'project',
              visibilityType: 'directory',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: queryParams.sortBy ?? null,
              defaultSortBy: DEFAULT_SORT,
          })
        : createEmptyFilterStates({
              visibilityType: 'directory',
              defaultSortBy: DEFAULT_SORT,
          });

    const appliedFilterCount = getAppliedFilterNumber(filterStates);

    // Determine if preloading is enabled (no ads/banners at top of grid)
    // We use the same condition for setting eager loading on tiles
    const hasCategoryPlacementAds = Boolean(ads?.CategoryPlacement?.length);
    const hasSponsoredAds = Boolean(sponsoredAds?.length);
    const shouldPreloadImages =
        appliedFilterCount > 0 ||
        (!hasCategoryPlacementAds && !hasSponsoredAds);
    const numberTilesToEagerLoad = !isMobile ? 3 : 2;

    // For high admins (BC-4449 rollout), fetch banner data from the live DB so
    // tiles can show which slide is the banner and carry the per-slide
    // LibraryImage IDs needed to set one.
    const isHighAdmin =
        user?.__typename === 'Me' &&
        'IsHighAdmin' in user &&
        user.IsHighAdmin === true;
    let bannerInfoByProject;
    if (
        isHighAdmin &&
        hasFeature(context.featureFlags, PROJECT_TILE_BANNER_SELECTOR_FLAG)
    ) {
        const projectHitIds =
            searchResult?.hits
                ?.filter((hit) => hit.PType === 'Project')
                .map((hit) => hit.id) ?? [];
        if (projectHitIds.length) {
            try {
                bannerInfoByProject = await getProjectBannerImages(
                    { ids: projectHitIds },
                    context,
                );
            } catch (error) {
                context.logger.warning(
                    `Failed to fetch project banner images: ${String(error)}`,
                );
            }
        }
    }

    const projects = getProjectsFromESSearchResults(
        searchResult,
        user,
        bannerInfoByProject,
    );
    const tiles: TileViewModel<ProjectTileViewModelRocco>[] = projects.map(
        (project, index) => ({
            aspectRatio: 'default',
            model: project,
            // Set eager loading for first N tiles to match preloading
            eager: shouldPreloadImages && index < numberTilesToEagerLoad,
        }),
    );

    const projectTiles =
        searchResult?.hits?.reduce((tiles, hit) => {
            if (hit.PType === 'Project')
                tiles.push(
                    mapESProjectTileViewModel(hit, 'medium', isMobile, user),
                );
            return tiles;
        }, [] as ProjectTileViewModel[]) ?? [];

    // Extract featured project IDs from query result
    const allFeaturedProjects =
        featuredProjectsResult &&
        'directoryFeaturedProjects' in featuredProjectsResult
            ? featuredProjectsResult.directoryFeaturedProjects
            : [];

    // Filter featured projects by current category path.
    // On root /projects page, show all featured projects.
    // On subcategory pages (e.g. /projects/commercial), only show featured
    // projects whose categories include the current path.
    const isRootProjectsPage =
        basePath === '/projects' || basePath === '/projects/';
    // Pinned projects always occupy the top slots but in a random order on
    // each page load, so no single pinned project monopolises the first tile
    const filteredFeaturedProjects = shuffleArray(
        isRootProjectsPage
            ? [...allFeaturedProjects]
            : allFeaturedProjects.filter((fp) =>
                  fp.Project?.Categories?.some((cat: { Link: string }) =>
                      cat.Link?.toLowerCase().startsWith(
                          basePath.toLowerCase(),
                      ),
                  ),
              ),
    );
    const featuredProjectIds = filteredFeaturedProjects.map(
        (fp) => fp.ProjectID,
    );

    // Pinned projects only take priority on the Featured (default) sort. For
    // any other sort (New, Trending, …) they sort naturally with the results.
    const isFeaturedSort =
        (queryParams.sortBy ?? DEFAULT_SORT) === DEFAULT_SORT;

    // If on page 1, inject any featured projects that aren't in the current
    // ES results so buildProjectTileGrid can move them to the front.
    if (page === 1 && isFeaturedSort) {
        // Process in reverse so the first featured project ends up at index 0
        for (let i = filteredFeaturedProjects.length - 1; i >= 0; i--) {
            const fp = filteredFeaturedProjects[i];
            if (!fp?.Project) continue;
            const isInResults = tiles.some(
                (tile) => tile.model.id === fp.ProjectID,
            );
            if (!isInResults) {
                const featuredTileModel = mapProjectToTileViewModel(
                    fp.Project,
                    { appDisplayMode, requireStaffAdmin: true },
                    undefined,
                    user,
                );
                tiles.unshift({
                    aspectRatio: 'default',
                    model: featuredTileModel,
                });
            }
        }
    }

    const shuffledAds = ads?.CategoryPlacement
        ? shuffleArray(ads.CategoryPlacement)
        : [];

    const verticalTileAds = ads?.VerticalTilePlacement
        ? shuffleArray(ads.VerticalTilePlacement)
        : [];
    const sponsoredAdsShuffled = sponsoredAds
        ? professionalEvenShuffleAds(sponsoredAds).slice(
              0,
              MAX_SPONSORED_AD_TILES_DISPLAYED,
          )
        : [];

    const sponsoredAdsTiles = sponsoredAdsShuffled.flatMap((advert) => {
        const professionalTile = advertCreativeProjectTileView(advert);
        return professionalTile !== null ? professionalTile : [];
    });

    const projectAdsTiles = ads ? getProjectPlacements(ads, isMobile) : [];

    const enableAwardBadges = hasFeature(featureFlags, 'award_badges');

    return json({
        userPreferenceSession: session.getUserPreferences(),
        appDisplayMode,
        directoryPageMeta,
        projectCategory,
        projectTiles,
        pagination,
        searchResult,
        recentlyViewedItems:
            recentlyViewedResult?.RecentlyViewedProjects?.map((project) =>
                mapProjectToTileViewModel(project, {
                    appDisplayMode,
                    imageToDisplay: 'featured',
                }),
            ) ?? [],
        filterStates,
        // If directory doesn't have a description, use the root category description as a fallback
        categoryDescription:
            directoryPageMeta?.directoryHeaderDescription ||
            rootDirectoryPageMeta?.directoryHeaderDescription ||
            `ArchiPro features the largest selection of completed architectural projects in ${fullCountryNameFromCode(context.localisation.country)}. These building and renovation accomplishments showcase the very best in design ingenuity and talent. Browse through these inspirational projects and connect with the professionals involved directly via ArchiPro.`,
        ads: shuffledAds,
        verticalTileAds,
        sponsoredAds: sponsoredAdsTiles,
        projectAds: projectAdsTiles.slice(0, MAX_ADS_PER_PAGE),
        localisation,
        enableAwardBadges,
        appliedFilterCount,
        url: url.pathname + url.search,
        heroPotm: _disableHeroPotm ? undefined : heroPotm,
        gridPotms,
        hasHeroPotmMedia: hasHeroPotmMedia,
        pageTheme: hasHeroPotmMedia ? 'transparent' : 'default',
        projectTilesRocco: tiles,
        searchIslandPlaceholders: mapCategoryCountToPlaceholder(
            activeDirectoryCategoriesCount?.projects ?? [],
            `Search projects`,
            'projects',
        ),
        featuredProjectIds,
        isFeaturedSort,
        rootCategories: mapCategoriesToTileViewModel(
            rootProjectCategoriesResult?.Categories?.edges ?? [],
        ),
        hideProfessionalsFilter,
    });
};

const ProjectsCategoryPage = () => {
    const { user } = useAppData();
    const { css } = useStyles();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const { isSafari } = useDeviceBrowserContext();
    const filtersRef = useRef<HTMLDivElement | null>(null);
    const {
        categoryDescription,
        directoryPageMeta,
        projectTiles,
        pagination,
        recentlyViewedItems,
        filterStates,
        ads,
        verticalTileAds,
        sponsoredAds,
        projectAds,
        enableAwardBadges,
        appliedFilterCount,
        heroPotm,
        gridPotms,
        hasHeroPotmMedia,
        projectTilesRocco,
        featuredProjectIds: loaderFeaturedProjectIds,
        isFeaturedSort,
        rootCategories,
        hideProfessionalsFilter,
    } = useLoaderData<typeof loader>();
    const tracker = useTracker();
    const [searchParams] = useSearchParams();
    const { setCategory } = useCurrentCategory();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(ads);
    const verticalTileAdsMemoized = useLocationMemo(verticalTileAds);
    const sponsoredAdsMemoized = useLocationMemo(sponsoredAds);
    const projectAdsMemoized = useLocationMemo(projectAds);
    const remixLocation = useLocation();
    const projectTilesRoccoAdmin = useTilesAdminData(
        projectTilesRocco as TileViewModel<ProjectTileViewModelRocco>[],
    );

    // Featured projects state + hook
    const { toast } = useToaster();
    const [optimisticFeaturedIds, setOptimisticFeaturedIds] = useState<
        number[]
    >(loaderFeaturedProjectIds);
    // Track tiles currently fading out (being unfeatured)
    const [unfeaturedIds, setUnfeaturedIds] = useState<Set<number>>(new Set());
    const unfeaturedTimersRef = useRef<
        Map<number, ReturnType<typeof setTimeout>>
    >(new Map());

    const {
        setInLibrary,
        handleRemoveFromLibrary,
        removedFromLibraryIds,
        unmountedFromLibraryIds,
    } = useLibraryRemovalUi({
        // The server auto-unpins projects leaving the directory — mirror it on
        // confirmed removals so the tile (and its undo-restored ghost) doesn't
        // keep a stale pinned state. Failed removals leave pin state untouched.
        onAfterRemove: (itemType, itemId) => {
            if (itemType === 'project') {
                setOptimisticFeaturedIds((prev) =>
                    prev.filter((id) => id !== itemId),
                );
            }
        },
    });

    // Sync optimistic state when loader data changes (navigation / revalidation)
    useEffect(() => {
        setOptimisticFeaturedIds(loaderFeaturedProjectIds);
    }, [loaderFeaturedProjectIds]);

    // Cleanup unfeature timers on unmount (library-removal timers handled by useLibraryRemovalUi)
    useEffect(() => {
        const timers = unfeaturedTimersRef.current;
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    const { toggleFeaturedProject } = useToggleFeaturedProject(
        // onError: revert optimistic update and clear any fade-out state
        (error) => {
            setOptimisticFeaturedIds(loaderFeaturedProjectIds);
            setUnfeaturedIds(new Set());
            unfeaturedTimersRef.current.forEach((timer) => clearTimeout(timer));
            unfeaturedTimersRef.current.clear();
            if (error.Message) {
                toast(error.Message, { type: 'error' });
            }
        },
        // onSuccess: show toast
        (_projectId, featured) => {
            toast(
                featured
                    ? 'Project pinned to top of directory'
                    : 'Project unpinned from directory',
                { type: 'success' },
            );
        },
    );

    // Track tiles that were just pinned (for fade-in animation)
    const [newlyFeaturedIds, setNewlyFeaturedIds] = useState<Set<number>>(
        new Set(),
    );

    const ANIMATION_DURATION = 500;

    const handleToggleFeature = useCallback(
        (projectId: number, feature: boolean) => {
            if (feature) {
                // Pin: add to featured + mark as "newly featured" for fade-in
                setNewlyFeaturedIds((prev) => new Set(prev).add(projectId));
                setOptimisticFeaturedIds((prev) => [projectId, ...prev]);
                // On next frame, remove from newlyFeaturedIds so CSS transitions to visible
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setNewlyFeaturedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(projectId);
                            return next;
                        });
                    });
                });
            } else {
                // Unpin: two-phase — fade out first, then remove
                setUnfeaturedIds((prev) => new Set(prev).add(projectId));

                const timer = setTimeout(() => {
                    setOptimisticFeaturedIds((prev) =>
                        prev.filter((id) => id !== projectId),
                    );
                    setUnfeaturedIds((prev) => {
                        const next = new Set(prev);
                        next.delete(projectId);
                        return next;
                    });
                    unfeaturedTimersRef.current.delete(projectId);
                }, ANIMATION_DURATION);

                unfeaturedTimersRef.current.set(projectId, timer);
            }
            toggleFeaturedProject?.(projectId, feature);
        },
        [toggleFeaturedProject],
    );

    // init category tiles tracking
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const projectsRef = useRef<HTMLDivElement>(null);
    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedRequirements = searchParams.getAll('services').join(' | ');
    const selectedProfessions = searchParams
        .getAll('professionals')
        .join(' | ');
    useEffect(() => {
        tracker.log('projectListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                requirement: selectedRequirements,
                professionals: selectedProfessions,
            },
        });
    }, [selectedCategory, selectedProfessions, selectedRequirements, tracker]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const projectIDs = projectTiles.map((project) => project.tile.projectID);
    const sponsoredAdsIDs = sponsoredAdsMemoized.map(
        (project) => project.projectId,
    );

    const {
        getTileTopActions,
        hotButtonModal,
        getTileAdminCaption,
        canEditAdmin,
    } = useTileHotButtons({
        itemType: 'project',
        itemIDs: [...projectIDs, ...sponsoredAdsIDs],
    });

    const isStaffUser = user.__typename === 'Me' && user.IsArchiproStaff;
    const showViewCount = useABTestIsOn('project_tile_stats') && isStaffUser;

    //potms feature flag conditions
    const directoryPotmEnbaled = useABTestIsOn('directory-potms');
    const potmFeatureOn = usePotmHeroEnabled();
    const heroPotmsEnabled = potmFeatureOn && heroPotm;

    // check if there are both cover image and videos available
    const showHeroPotm = !!heroPotmsEnabled && hasHeroPotmMedia;

    const searchFilters = useSearchFilters({
        category: 'project',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: getProjectCategoryIgnoreFilterKeys(
            hideProfessionalsFilter,
        ),
    });

    const apConnectBannerFlag = useFeature('archipro_connect_category_banner');

    const showProfessionalArchiproBanner =
        getShowProfessionalArchiproConnectBanner(user);

    // Drop tiles whose fade-out animation has finished so the grid reflows
    // and fills the gap left by removed-from-library tiles.
    const visibleProjectTilesRocco = useMemo(
        () =>
            projectTilesRoccoAdmin.filter(
                (t) => !unmountedFromLibraryIds.has(t.model.id),
            ),
        [projectTilesRoccoAdmin, unmountedFromLibraryIds],
    );

    const sortedGridTiles = buildProjectTileGrid({
        showArchiproConnectBanner:
            showProfessionalArchiproBanner && apConnectBannerFlag,
        desktop,
        ads: adsMemoized,
        verticalTileAds: verticalTileAdsMemoized,
        sponsoredAds: sponsoredAdsMemoized,
        projectAds: projectAdsMemoized,
        potmFeatureOn: directoryPotmEnbaled,
        projectTiles: visibleProjectTilesRocco,
        gridPotms,
        appliedFilterCount,
        pagination,
        columnCount: desktop ? (filterDrawerOpen ? 3 : 4) : 1,
        featuredProjectIds:
            pagination.page === 1 && isFeaturedSort
                ? optimisticFeaturedIds
                : [],
    });

    const tileGridItems: ReactElement[] = renderProjectTileGrid({
        tiles: sortedGridTiles,
        mobile,
        getTileTopActions,
        willDelete,
        getTileAdminCaption,
        filterDrawerOpen,
        desktop,
        showViewCount,
        enableAwardBadges,
        canEditAdmin,
        featuredProjectIds: pagination.page === 1 ? optimisticFeaturedIds : [],
        unfeaturedIds,
        newlyFeaturedIds,
        removedFromLibraryIds,
        onToggleFeature: toggleFeaturedProject
            ? handleToggleFeature
            : undefined,
        onRemoveFromLibrary: setInLibrary ? handleRemoveFromLibrary : undefined,
    }) as ReactElement[];

    useEffect(() => {
        setCategory('Projects');
        return () => setCategory(null);
    }, [setCategory]);

    return (
        <Box>
            {showHeroPotm &&
                (desktop ? (
                    <DirectoryHeroPotmDesktop
                        {...heroPotm}
                        projectsCount={pagination.totalCount}
                        projectsRef={projectsRef}
                    />
                ) : (
                    <DirectoryHeroPotmMobile
                        {...heroPotm}
                        projectsCount={pagination.totalCount}
                        projectsRef={projectsRef}
                    />
                ))}
            <Container
                ref={projectsRef}
                className={css(S.ProjectCategoryContainer)}
            >
                {desktop ? (
                    <TitleSectionDesktop
                        category={directoryPageMeta.category}
                        rootCategory="Projects."
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates.totalCount}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        showCategoryTitle={!heroPotmsEnabled}
                        isRoot={directoryPageMeta.isRoot}
                    />
                ) : (
                    <TitleSectionMobile
                        title={directoryPageMeta.category}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                        showCategoryTitle={!showHeroPotm}
                    >
                        <TitleSectionFilterMobile
                            esCategory="project"
                            searchFilters={searchFilters}
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="project"
                        searchFilters={searchFilters}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        filterDrawerOpen={filterDrawerOpen}
                        componentBefore={
                            <div ref={filtersRef}>
                                <CollapsibleToggle
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                />
                            </div>
                        }
                    />
                )}

                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="project"
                                searchFilters={searchFilters}
                                hideCategorySectionTitle={true}
                                hideCategoryBackButton={false}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div>
                        {desktop &&
                            searchFilters.appliedFilters &&
                            searchFilters.appliedFilters.length > 0 && (
                                <AppliedFilters
                                    filterItems={searchFilters.appliedFilters}
                                    resetFilters={() =>
                                        searchFilters.resetFilters(
                                            resetFilterStates(
                                                searchFilters.filterStates,
                                                ['category'],
                                            ),
                                        )
                                    }
                                />
                            )}

                        {sortedGridTiles.length ? (
                            <VirtualizedGrid
                                items={tileGridItems}
                                renderItem={(item: React.ReactNode) => item}
                                className={cn(
                                    'grid transition-all duration-500 ease-in-out',
                                    filterDrawerOpen
                                        ? 'grid-cols-1 md:grid-cols-3'
                                        : 'grid-cols-1 md:grid-cols-4',
                                    'gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20',
                                )}
                                enabled={isSafari}
                                initialVisibleCount={15}
                                batchSize={10}
                                loadMoreThreshold={0.7}
                                reduceThreshold={0.3}
                                minVisibleCount={15}
                                startMarker={
                                    <div className="hidden">
                                        <CollapsibleToggle
                                            filterDrawerOpen={filterDrawerOpen}
                                            toggleFilterDrawer={
                                                toggleFilterDrawer
                                            }
                                        />
                                    </div>
                                }
                                endMarker={
                                    pagination.totalPages > 1 && (
                                        <div className="pt-[6.5625rem] md:pt-20">
                                            <TilePagination
                                                currentPath={remixLocation}
                                                current={pagination.page}
                                                total={pagination.totalPages}
                                                urlBuilder={buildPaginationUrl}
                                                visibleCount={
                                                    appDisplayMode.desktop
                                                        ? 4
                                                        : 3
                                                }
                                            />
                                        </div>
                                    )
                                }
                            />
                        ) : (
                            <NoResultsSection resultsType={'projects'} />
                        )}
                    </div>
                </CollapsiblePageLayout>

                <div className="my-10 flex flex-col gap-10 md:mb-20 md:mt-17.5 md:gap-17.5">
                    <CategoryPageRecentlyViewed
                        displayMode={
                            appDisplayMode.desktop ? 'desktop' : 'mobile'
                        }
                        items={recentlyViewedItems}
                    />

                    {desktop && (
                        <ExploreAllCategories
                            categories={rootCategories}
                            title="Explore all project categories"
                        />
                    )}

                    <CategoryPageFAQ
                        faqSchemas={directoryPageMeta.faqSchemas}
                        footerContent={directoryPageMeta.footerContent}
                        variant={desktop ? 'grid' : undefined}
                        categoryLabel={
                            desktop ? 'ArchiPro Projects' : undefined
                        }
                    />
                </div>

                {itemIDToDelete && (
                    <DeleteItemConfirmModal
                        title={'Delete project'}
                        content={
                            'Are you sure you would like to delete this project? All information and associated tags will be lost.'
                        }
                        open={true}
                        onCancel={cancelDelete}
                        onConfirm={confirmDelete}
                    />
                )}
                {hotButtonModal}
            </Container>
        </Box>
    );
};

export default ProjectsCategoryPage;
